/* eslint-disable @typescript-eslint/no-explicit-any */
import { toast } from "react-toastify";
import useMounted from "@/hooks/mounted";
import { useEffect, useState } from "react";
import { useLocationHistoryStack } from "@/hooks/locationHistoryStack"
import { ICategoryEquipment, ICustomFieldPayload } from "@/interfaces/equipmentCategories";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
    createCategoryEquipment,
    listCategoryEquipments,
    showCategoryEquipment,
    updateCategoryEquipment,
    addCustomFieldToCategory,
    updateCustomField,
    deleteCustomFieldToCategory
} from "@/services/equipments/categoryEquipmentService";

const DEFAULT_DATA: ICategoryEquipment = {
    id: 0,
    name: "",
    isActive: true,
};

export const useEquipmentCategories = ({
    filter: filterInitial,
    disabledGetAll,
    disabledShowId,
    defaultLimit,
    includes = [],
    idCategoryEquipment,
}: {
    disabledGetAll?: boolean;
    disabledShowId?: boolean;
    defaultLimit?: number;
    filter?: Record<string, any>;
    includes?: string[];
    idCategoryEquipment?: string;
}) => {
    const location = useLocation();
    const { idCategoryEquipment: id } = useParams<{ idCategoryEquipment: string }>();
    const [categoryEquipments, setCategoryEquipments] = useState<ICategoryEquipment[]>([]);
    const [categoryEquipment, setCategoryEquipment] = useState<ICategoryEquipment>(DEFAULT_DATA);
    const { filter, handleFilter, page, setPage, limit, setLimit } =
      useEntityQueryParams({ filter: filterInitial, defaultLimit });
    const navigate = useNavigate();
    const [totalItems, setTotalItems] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const { goAdd, goEdit, goBack, pathBack } = useEntityNavigation({ basePath: "/towermanagement/equipments/categoryEquipment" });

    const handleCategoryEquipment = <K extends keyof ICategoryEquipment>(key: K, value: ICategoryEquipment[K]) => {
        setCategoryEquipment((current) => ({ ...current, [key]: value }));
    };

    const getAll = ({ page = 1, limit, filter: filterG = {}, sort }: { page?: number; limit?: number; filter?: Record<string, any>; sort?: any }) => {
        setLoading(true);
        listCategoryEquipments({ includes: [...includes], filter: { ...filter, ...filterG }, page, limit, sort })
            .then(({ categoryEquipments, limit, currentPage, totalItems }) => {
                setCategoryEquipments(categoryEquipments);
                
                setLimit(Number(limit));
                setTotalItems(Number(totalItems));
            })
            .finally(() => setLoading(false));
    };

    const search = useEntitySearch({
      list: listCategoryEquipments,
      key: "categoryEquipments",
      includes,
      filter,
    });


    const get = ({ id }: { id: string | number }) => {
        setLoading(true);
        showCategoryEquipment({ id })
            .then(({ categoryEquipment: categoryEquipmentShow }) => setCategoryEquipment(categoryEquipmentShow))
            .finally(() => setLoading(false));
    };

    const create = (closeModalOnSuccess = true) => {
        setLoading(true);
        createCategoryEquipment({ categoryEquipment })
            .then(({ categoryEquipment: createdCategory }) => {
                toast.success("Categoria de equipamento criada com sucesso!");
                setCategoryEquipment(createdCategory);
                if (closeModalOnSuccess) {
                    navigate(pathBack);
                } else {
                    navigate(`/towermanagement/equipments/categoryEquipment/${createdCategory.id}`);
                }
            })
            .finally(() => setLoading(false));
    };

    const update = () => {
        if (!categoryEquipment?.id) return;
        setLoading(true);
        updateCategoryEquipment({ id: categoryEquipment.id, categoryEquipment })
            .then(() => {
                toast.success("Categoria de equipamento atualizada com sucesso!");
                navigate(pathBack)
            })
            .finally(() => setLoading(false));
    };

    const addCustomField = (customField: ICustomFieldPayload) => {
        if (!categoryEquipment?.id) return Promise.reject(new Error("Categoria não encontrada"));
        setLoading(true);
        return addCustomFieldToCategory({ id: categoryEquipment.id, customField })
            .then(() => {
                toast.success("Campo personalizado adicionado com sucesso!");
                get({ id: categoryEquipment.id });
            })
            .catch((err: any) => {
                const message = err?.response?.data?.message || err?.message || "Erro ao adicionar campo personalizado";
                toast.error(message);
            })
            .finally(() => setLoading(false));
    };

    // ADICIONADO O type AQUI PARA O PAYLOAD
    const editCustomField = (customFieldId: string | number, name: string, type: string, options?: string[]) => {
        if (!categoryEquipment?.id) return Promise.reject(new Error("Categoria não encontrada"));
        setLoading(true);
        return updateCustomField({ categoryId: categoryEquipment.id, customFieldId, name, type, options } as any)
            .then(() => {
                toast.success("Campo personalizado atualizado com sucesso!");
                get({ id: categoryEquipment.id });
            })
            .catch(() => {
                toast.error("Erro ao atualizar campo personalizado");
            })
            .finally(() => setLoading(false));
    };

    const deleteCustomField = (customFieldId: string | number) => {
        if (!categoryEquipment?.id) return Promise.reject(new Error("Categoria não encontrada"));
        setLoading(true);
        return deleteCustomFieldToCategory({ id: categoryEquipment.id, customFieldId })
            .then(() => {
                toast.success("Campo personalizado deletado com sucesso!");
                get({ id: categoryEquipment.id });
            })
            .catch(() => {
                toast.error("Erro ao deletar campo personalizado");
            })
            .finally(() => setLoading(false));
    };

    useMounted(() => {
        getAll({ page, limit, filter });
    }, [page, limit, filter]);

    useEffect(() => {
        const isPage = location.pathname.includes("/towermanagement/equipments/categoryEquipment");
        const targetId = idCategoryEquipment || id;

        if (targetId && (isPage || idCategoryEquipment) && !disabledShowId) {
            if (targetId !== "new") get({ id: targetId });
            setCategoryEquipment(DEFAULT_DATA);
            return;
        }

        if (!disabledGetAll) getAll({ limit });
    }, [idCategoryEquipment, id, disabledGetAll, disabledShowId]);

    return {
        categoryEquipments,
        categoryEquipment,
        handleCategoryEquipment,
        loading,
        setPage,
        setLimit,
        page,
        limit,
        goEdit,
        goBack,
        goAdd,
        create,
        id,
        update,
        totalItems,
        search,
        handleFilter,
        addCustomField,
        editCustomField,
        deleteCustomField,
    };
};